<script lang="ts">
	import ExpandedFeatureCard from '$lib/components/ui/ExpandedFeatureCard.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import {
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		type CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { NavLink } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import IconArrowRight from '~icons/mdi/arrow-right';

	const {
		featureType,
		items,
		header,
		location,
		ctaText,
		seeAll,
		sectionClasses
	}: {
		featureType: CityFeatureType;
		items: CityFeaturePreview[];
		header?: { title?: string; subText: string; Icon?: Component };
		location: PlaceLocation;
		ctaText?: string;
		seeAll?: {
			nav: NavLink;
			text: string;
		};
		sectionClasses?: ClassValue;
	} = $props();
</script>

<section class={sectionClasses}>
	<div
		class="mb-6 flex flex-col items-center justify-center border-b border-base-content/10 pb-4 md:flex-row md:items-end md:justify-between"
	>
		{@render Header()}
	</div>
	<ul
		class={{
			'mt-5 grid list-none grid-cols-1 gap-5 p-0': true,
			'md:grid-cols-2': items.length <= 2,
			'md:grid-cols-3': items.length > 2
		}}
	>
		{#each items as item (item.id)}
			{const images = $derived<CoverImage[]>(item.images)}
			<li>
				<ExpandedFeatureCard feature={item} {images} {location} ctaText={ctaText ?? 'view'} />
			</li>
		{/each}
	</ul>
	{#if seeAll !== undefined}
		<a
			class="group btn mt-5 w-full rounded-full font-mono btn-md btn-primary"
			href={`/${seeAll.nav.href}`}
			data-sveltekit-preload-data={seeAll.nav.preloadData}
			data-sveltekit-preload-code={seeAll.nav.preloadCode}
		>
			<span>{seeAll.text}</span>
			<IconArrowRight class="transition-transform duration-200 group-hover:translate-x-1" />
		</a>
	{/if}
</section>

{#snippet Header()}
	{const Icon = $derived<Component>(header?.Icon ?? CITY_FEATURE_TYPE_ICON_MAP[featureType])}
	{const title = $derived<string>(header?.title ?? `Other ${CITY_FEATURE_TYPE_LABELS[featureType]}`)}
	{const subText = $derived<string>(
		header?.subText ?? `Explore other ${CITY_FEATURE_TYPE_LABELS[featureType].toLowerCase()} in ${location.city.label}`
	)}

	<h2 class="flex flex-col items-center justify-center gap-2 text-xl md:flex-row md:text-3xl">
		<Icon class="text-xl text-primary md:text-3xl" />
		<span>{title}</span>
	</h2>
	<span>
		{subText}
	</span>
{/snippet}
